/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";

const Callout = ({ children }) => (
  <div className="my-7 p-5 border-l-4 border-[#bc6c25] bg-[#bc6c25]/5 rounded-r-2xl text-[#1a2421] text-[15px] leading-relaxed">
    {children}
  </div>
);

const Quote = ({ children, author }) => (
  <blockquote className="my-7 pl-6 border-l-2 border-[#2c4a3b]/30 italic text-[#1a2421] text-lg leading-relaxed font-serif">
    "{children}"
    {author && <footer className="mt-2 not-italic text-xs uppercase tracking-[0.18em] text-[#5c6b61]">— {author}</footer>}
  </blockquote>
);

const TOC = ({ items }) => (
  <nav className="my-7 p-5 bg-[#f0ebe1] rounded-2xl border border-[#2c4a3b]/8 not-prose">
    <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">En este artículo</p>
    <ol className="mt-3 space-y-1.5 text-sm text-[#1a2421]">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2">
          <span className="text-[#bc6c25] font-bold">{i + 1}.</span>
          <a href={`#${it.id}`} className="hover:text-[#bc6c25]">{it.label}</a>
        </li>
      ))}
    </ol>
  </nav>
);

export const ARTICLE_CONTENT_3 = {
  /* =================================================================
     7. Immunocal Platinum: la fórmula para mayores de 40
     ================================================================= */
  "immunocal-platinum-beneficios": (
    <>
      <p>A partir de los 40 años, el cuerpo empieza a jugar una partida nueva. La producción de glutatión cae a un ritmo medible cada década, los huesos pierden densidad de forma silenciosa, el equilibrio mineral se altera, y la energía deja de ser "ilimitada como antes". <strong>Immunocal Platinum</strong> fue diseñado exactamente para esta etapa.</p>
      <p>En este artículo te explico qué tiene que la fórmula Original no tiene, por qué importan esos extras a partir de los 40, y para quién es realmente esta presentación.</p>

      <TOC items={[
        { id: "que-es", label: "Qué es Immunocal Platinum" },
        { id: "diferencias", label: "Diferencias vs Immunocal Original" },
        { id: "potasio", label: "Citrato de potasio: el alcalinizante silencioso" },
        { id: "magnesio", label: "Citrato de magnesio: el mineral subestimado" },
        { id: "vitamina-k", label: "Vitamina K: dirigir el calcio donde toca" },
        { id: "para-quien", label: "Para quién es Immunocal Platinum" },
        { id: "como-tomarlo", label: "Cómo tomarlo correctamente" },
        { id: "combinaciones", label: "Combinaciones que potencian sus efectos" },
      ]} />

      <h2 id="que-es">Qué es Immunocal Platinum</h2>
      <p>Es la <strong>versión avanzada</strong> de Immunocal, la proteína aislada de suero no desnaturalizada patentada por el Dr. Gustavo Bounous. Mantiene 100 % de los beneficios de la fórmula Original como precursor de <Link to="/glutation">glutatión</Link>, y añade tres elementos pensados para las necesidades específicas de adultos a partir de los 40.</p>
      <p>Su patente y respaldo clínico son los mismos: <strong>más de 30 años de investigación</strong>, listado en el CPS canadiense y el PDR estadounidense.</p>

      <h2 id="diferencias">Diferencias vs Immunocal Original</h2>
      <ul>
        <li><strong>Tamaño del sobre:</strong> Original 10 g, Platinum 25 g.</li>
        <li><strong>Proteína por sobre:</strong> Original 9 g, Platinum 20 g.</li>
        <li><strong>Citrato de potasio:</strong> solo en Platinum.</li>
        <li><strong>Citrato de magnesio:</strong> solo en Platinum.</li>
        <li><strong>Vitamina K1:</strong> solo en Platinum.</li>
        <li><strong>pH urinario:</strong> Platinum tiene efecto alcalinizante leve.</li>
        <li><strong>Precio por sobre:</strong> Platinum es más alto, pero rinde más por la cantidad de proteína.</li>
      </ul>

      <Callout>
        <strong>Mismo "motor", upgrades premium:</strong> piensa en Original como un auto excelente, y en Platinum como ese mismo auto con suspensión deportiva, dirección asistida y aire acondicionado. Sirven al mismo propósito; uno se adapta mejor a quien recorre carreteras más exigentes.
      </Callout>

      <h2 id="potasio">Citrato de potasio: el alcalinizante silencioso</h2>
      <p>La dieta moderna —ultraprocesados, carnes en exceso, refrescos, bajo consumo de vegetales— tiende a generar una <strong>carga ácida</strong> sostenida en el cuerpo. Para neutralizarla, el organismo recurre a sus "reservas" de minerales alcalinos, principalmente al <strong>calcio óseo</strong>.</p>
      <p>El citrato de potasio:</p>
      <ul>
        <li>Alcaliniza el pH urinario (reduce la pérdida de calcio por orina).</li>
        <li>Preserva masa ósea, especialmente en posmenopausia.</li>
        <li>Apoya la función muscular y nerviosa.</li>
        <li>Reduce el riesgo de cálculos renales de oxalato cálcico.</li>
      </ul>
      <p>Estudios muestran que mujeres posmenopáusicas que consumen citrato de potasio regular reducen la <strong>resorción ósea</strong> de forma significativa.</p>

      <h2 id="magnesio">Citrato de magnesio: el mineral subestimado</h2>
      <p>El magnesio participa en <strong>más de 300 reacciones enzimáticas</strong>. Sin embargo, según múltiples encuestas nutricionales, entre 50 % y 70 % de adultos no alcanza los requerimientos diarios. Las consecuencias del déficit suelen pasar desapercibidas porque son inespecíficas:</p>
      <ul>
        <li>Calambres y tensión muscular.</li>
        <li>Dificultad para dormir profundo.</li>
        <li>Ansiedad de baja intensidad.</li>
        <li>Estreñimiento.</li>
        <li>Migrañas tensionales.</li>
        <li>Palpitaciones leves.</li>
      </ul>
      <p>El citrato de magnesio tiene <strong>biodisponibilidad superior</strong> al óxido o cloruro común. Apoya la salud muscular, ósea, nerviosa y cardiovascular.</p>

      <h2 id="vitamina-k">Vitamina K1: dirigir el calcio donde toca</h2>
      <p>La vitamina K es esencial para activar la <strong>osteocalcina</strong>, la proteína que fija el calcio en los huesos. Sin vitamina K suficiente, el calcio "navega" libre en sangre y tiende a depositarse donde <em>no</em> debe: paredes arteriales, articulaciones, tejidos blandos.</p>
      <p>Por eso muchos protocolos modernos de salud ósea recomiendan vitamina D <strong>combinada con K</strong>. Platinum aporta una dosis estable de K1.</p>

      <Callout>
        <strong>Tip avanzado:</strong> para una salud ósea óptima, combina Immunocal Platinum con un suplemento adicional de vitamina K2 MK-7 (45-90 mcg/día) y vitamina D3 (2,000-5,000 UI/día).
      </Callout>

      <h2 id="para-quien">Para quién es Immunocal Platinum</h2>
      <ul>
        <li><strong>Mujeres 40+, peri-menopausia y menopausia.</strong> La caída estrogénica acelera la pérdida ósea; Platinum apoya la prevención.</li>
        <li><strong>Hombres 40+, andropausia.</strong> La testosterona también baja, lo que impacta masa muscular y ósea.</li>
        <li><strong>Atletas masters.</strong> Recuperación más lenta, mayor riesgo de fracturas por estrés.</li>
        <li><strong>Personas con historia familiar de osteopenia/osteoporosis.</strong></li>
        <li><strong>Quienes consumen mucha carne o refrescos.</strong> Carga ácida elevada.</li>
        <li><strong>Personas con calambres o sueño superficial.</strong> Probablemente deficiencia de magnesio.</li>
        <li><strong>Adultos mayores con función inmune debilitada.</strong></li>
      </ul>

      <h2 id="como-tomarlo">Cómo tomarlo correctamente</h2>
      <ul>
        <li><strong>1 sobre al día</strong> en agua fría o jugo natural sin pulpa, en ayunas (preferible).</li>
        <li><strong>No usar batidora ni licuadora.</strong> La agitación violenta puede desnaturalizar las proteínas. Mezclar con cuchara o agitando suavemente.</li>
        <li><strong>No usar líquidos calientes.</strong> Mismo motivo. Agua a temperatura ambiente o fría.</li>
        <li><strong>Esperar 20-30 min antes de comer.</strong> Para mejor absorción.</li>
        <li><strong>Para mayor efecto:</strong> 1 sobre por la mañana + 1 a media tarde.</li>
        <li>Si combinas con café, espera 30 min entre uno y otro.</li>
      </ul>

      <h2 id="combinaciones">Combinaciones que potencian sus efectos</h2>
      <ul>
        <li><strong>+ Omega-3 EPA/DHA:</strong> sinergia antiinflamatoria y cardio-cerebral. Ver <Link to="/blog/omega-gen-v-5-en-1">Omega Gen V</Link>.</li>
        <li><strong>+ Vitamina D3 + K2:</strong> protección ósea integral.</li>
        <li><strong>+ Probióticos:</strong> mejora absorción mineral.</li>
        <li><strong>+ Movimiento de carga:</strong> el ejercicio con peso es <em>indispensable</em> para hueso fuerte. Suplementos sin ejercicio dan resultados parciales.</li>
      </ul>

      <Quote author="Sarai Núñez">
        A los 40 no se trata de "envejecer con dignidad" sino de <em>florecer con propósito</em>. La diferencia está en las decisiones que tomas a los 35 y a los 45.
      </Quote>

      <p>Si quieres saber si Platinum es la presentación correcta para ti, <Link to="/#quiz">haz nuestro quiz wellness</Link>. En 3 minutos tendrás un protocolo personalizado.</p>
      <p>Para ver precios actualizados y opciones de Kit de Inicio, revisa <Link to="/blog/immunocal-precio-mexico-2026">Immunocal precio México 2026</Link> y nuestra <Link to="/immunocal-precio">página de precios</Link>.</p>
    </>
  ),

  /* =================================================================
     8. Immunocal Sport para atletas
     ================================================================= */
  "immunocal-sport-para-atletas": (
    <>
      <p>Si entrenas en serio —corres, ruedas, levantas, juegas fútbol o haces CrossFit— tu cuerpo está sometido a un nivel de <strong>estrés oxidativo</strong> que el sedentario nunca experimenta. Y aquí está la paradoja del deporte: <em>el mismo ejercicio que te hace fuerte también te puede degastar si no recuperas bien</em>. Immunocal Sport está pensado exactamente para resolver esa ecuación.</p>

      <TOC items={[
        { id: "estres-deporte", label: "El estrés oxidativo del entrenamiento intenso" },
        { id: "composicion", label: "Qué contiene Immunocal Sport" },
        { id: "beneficios", label: "Los 5 beneficios para atletas" },
        { id: "cuando", label: "Cuándo y cómo tomarlo" },
        { id: "comparativa", label: "Sport vs. Original: cuál te conviene" },
        { id: "errores", label: "Errores comunes en suplementación deportiva" },
        { id: "combo", label: "El combo de oro Immunotec para atletas" },
      ]} />

      <h2 id="estres-deporte">El estrés oxidativo del entrenamiento intenso</h2>
      <p>Cuando entrenas duro, tus mitocondrias generan ATP a un ritmo elevadísimo. Como subproducto, producen <strong>especies reactivas de oxígeno (ROS)</strong> en cantidades mucho mayores que en reposo. Una dosis controlada es buena (señaliza adaptaciones positivas); una dosis excesiva sin recuperación adecuada lleva a:</p>
      <ul>
        <li>Recuperación lenta.</li>
        <li>Estancamiento de rendimiento.</li>
        <li>Lesiones por sobreuso.</li>
        <li>Inmunosupresión post-entrenamiento (la famosa "ventana abierta" donde te enfermas más fácil).</li>
        <li>Inflamación articular crónica.</li>
      </ul>
      <p>La estrategia: <strong>elevar tu sistema antioxidante endógeno</strong>, no inundarte de antioxidantes externos (que pueden incluso bloquear adaptaciones al ejercicio si se toman cerca del entrenamiento).</p>

      <h2 id="composicion">Qué contiene Immunocal Sport</h2>
      <ul>
        <li><strong>Proteína aislada de suero no desnaturalizada</strong> (precursor de glutatión, igual que Immunocal Original).</li>
        <li><strong>Electrolitos:</strong> sodio, potasio, magnesio (reposición post-sudor).</li>
        <li><strong>Creatina monohidratada:</strong> el suplemento deportivo con más evidencia del mundo. Aumenta potencia, recuperación e incluso función cognitiva.</li>
        <li><strong>Carbohidratos de absorción rápida</strong> en dosis controlada (favorecen la repleción de glucógeno).</li>
        <li><strong>Sabor cítrico suave</strong>, sin azúcares ni edulcorantes artificiales agresivos.</li>
      </ul>

      <h2 id="beneficios">Los 5 beneficios para atletas</h2>

      <h3>1. Recuperación muscular acelerada</h3>
      <p>El glutatión elevado reduce el daño oxidativo post-entreno y permite que el músculo repare más rápido. La creatina aporta sustrato energético para resíntesis de ATP.</p>

      <h3>2. Menor inflamación post-entrenamiento</h3>
      <p>Menos dolor, menos hinchazón, retorno más rápido al siguiente entrenamiento. Esto se traduce en más sesiones de calidad por semana.</p>

      <h3>3. Sistema inmune preservado</h3>
      <p>El entrenamiento intenso deprime temporalmente el sistema inmune. El glutatión es clave en la función de linfocitos T. Resultado: menos resfriados que te obliguen a frenar tu progresión.</p>

      <h3>4. Hidratación efectiva</h3>
      <p>Los electrolitos correctamente dosificados reponen lo perdido en sudor, evitan calambres y mantienen rendimiento en sesiones largas.</p>

      <h3>5. Potencia y rendimiento</h3>
      <p>La creatina mejora la capacidad de generar fuerza en esfuerzos cortos e intensos (sprints, levantamientos, saltos). Beneficio comprobado en cientos de estudios.</p>

      <h2 id="cuando">Cuándo y cómo tomarlo</h2>
      <Callout>
        <strong>Ventana ideal:</strong> entre 0 y 60 minutos <em>después</em> del entrenamiento. Tu cuerpo está en estado anabólico, las células absorben nutrientes con máxima eficiencia.
      </Callout>
      <p>Sugerencias prácticas:</p>
      <ul>
        <li>1 sobre disuelto en agua fría, post-entreno.</li>
        <li>Para sesiones largas (&gt;90 min): puedes preparar un segundo sobre y consumirlo durante.</li>
        <li>En días sin entrenamiento, sigue tomando 1 sobre al día (mantener creatina y glutatión).</li>
        <li>No mezclar con licuadora — agitar suavemente.</li>
      </ul>

      <h2 id="comparativa">Sport vs. Original: cuál te conviene</h2>
      <ul>
        <li><strong>Entrenas &lt;3 veces/semana, intensidad moderada:</strong> Original es suficiente.</li>
        <li><strong>Entrenas 4-6 veces/semana, intensidad alta:</strong> Sport post-entreno + Original en ayunas.</li>
        <li><strong>Atleta de competencia o larga distancia:</strong> Sport como base, doble dosis en bloques de carga.</li>
        <li><strong>Quieres beneficio inmunológico/antiedad general, no atlético:</strong> Original.</li>
      </ul>

      <h2 id="errores">Errores comunes en suplementación deportiva</h2>
      <ol>
        <li><strong>Tomar mega-dosis de antioxidantes cerca del entrenamiento.</strong> Pueden anular adaptaciones. El glutatión vía precursores es la excepción porque es endógeno.</li>
        <li><strong>Olvidar electrolitos.</strong> Tomar solo agua en sesiones largas baja el sodio en sangre.</li>
        <li><strong>Consumir whey común y creer que es lo mismo.</strong> No lo es —ver <Link to="/blog/immunocal-vs-proteina-whey">comparativa</Link>.</li>
        <li><strong>No descansar.</strong> Los suplementos no compensan falta de sueño y días off.</li>
        <li><strong>Pensar que se necesita "cargar" creatina.</strong> Los 5 g/día constantes funcionan igual.</li>
      </ol>

      <h2 id="combo">El combo de oro Immunotec para atletas</h2>
      <ul>
        <li><strong>Mañana:</strong> Immunocal Original (precursor de glutatión, en ayunas).</li>
        <li><strong>Post-entreno:</strong> Immunocal Sport (recuperación + electrolitos + creatina).</li>
        <li><strong>Diario:</strong> Omega Gen V con la comida (reduce inflamación articular). Ver <Link to="/blog/omega-gen-v-5-en-1">guía</Link>.</li>
        <li><strong>Antes de entrenar:</strong> XTRIVA si entrenas por la tarde y necesitas energía limpia. Ver <Link to="/blog/xtriva-energia-natural">guía XTRIVA</Link>.</li>
      </ul>

      <Quote author="Sarai Núñez">
        El atleta que entiende la recuperación juega un partido distinto: gana en silencio, los días que no entrena.
      </Quote>

      <p>¿Quieres saber qué combinación es ideal para tu deporte y volumen? <Link to="/#quiz">Haz nuestro quiz wellness</Link> y recibe un protocolo personalizado de 3 meses.</p>
    </>
  ),

  /* =================================================================
     9. XTRIVA: energía natural
     ================================================================= */
  "xtriva-energia-natural": (
    <>
      <p>Si dependes de 4 cafés al día o de bebidas energéticas industriales para "sobrevivir" la jornada, tu sistema nervioso está pagando un costo silencioso pero alto. <strong>XTRIVA</strong> es la alternativa que diseñó Immunotec: energía sostenida, foco mental, cero "bajón", y sin destruir tus glándulas suprarrenales.</p>
      <p>En este artículo te cuento qué contiene, cómo funciona y para quién es la mejor opción.</p>

      <TOC items={[
        { id: "problema", label: "El problema de los energéticos comerciales" },
        { id: "que-es", label: "Qué es XTRIVA" },
        { id: "ingredientes", label: "Ingredientes clave y por qué importan" },
        { id: "diferencia", label: "Diferencias vs. Red Bull, Monster y similares" },
        { id: "para-quien", label: "Para quién está pensado" },
        { id: "como-tomarlo", label: "Cómo y cuándo tomarlo" },
        { id: "transicion", label: "Cómo hacer la transición desde el café/energéticos" },
      ]} />

      <h2 id="problema">El problema de los energéticos comerciales</h2>
      <p>Las bebidas energéticas tradicionales (Red Bull, Monster, Vive 100 y demás) funcionan, sí. Pero el precio biológico es alto:</p>
      <ul>
        <li><strong>Azúcares o edulcorantes artificiales</strong> en dosis altas.</li>
        <li><strong>Cafeína sintética</strong> en cantidades agresivas (200-300 mg por lata).</li>
        <li><strong>Saborizantes y colorantes</strong> sin valor nutricional.</li>
        <li><strong>Taurina sintética</strong> sin contexto de cofactores.</li>
        <li><strong>Acidez extrema</strong> (pH ~3) que erosiona el esmalte dental.</li>
        <li><strong>Pico y caída de glucosa</strong> que termina en fatiga rebote.</li>
      </ul>
      <p>El resultado típico: energía durante 90 min, bajón pesado a las 2-3 h, dependencia creciente, sensibilidad reducida a la cafeína natural.</p>

      <h2 id="que-es">Qué es XTRIVA</h2>
      <p>XTRIVA es una <strong>bebida en sobre</strong> que combina cafeína natural derivada del guaraná, adaptógenos (hierbas que regulan el estrés), aminoácidos para enfoque mental y vitaminas del complejo B. Sin azúcar, sin colorantes artificiales, sin acidez agresiva.</p>

      <h2 id="ingredientes">Ingredientes clave y por qué importan</h2>

      <h3>Guaraná (cafeína natural)</h3>
      <p>El guaraná aporta cafeína de liberación más lenta que el café común. Resultado: energía estable durante 4-6 horas, sin pico exagerado ni bajón abrupto.</p>

      <h3>Ginseng siberiano (Eleuthero)</h3>
      <p>Adaptógeno clásico: <strong>regula la respuesta al estrés</strong> en lugar de estimular directamente. Mejora la resistencia mental, reduce la fatiga inducida por estrés crónico.</p>

      <h3>L-tirosina + L-fenilalanina</h3>
      <p>Aminoácidos precursores de <strong>dopamina y norepinefrina</strong> — los neurotransmisores del foco, la motivación y la atención. Útiles en jornadas de alto demanda cognitiva.</p>

      <h3>Vitaminas B6 y B12</h3>
      <p>Cofactores indispensables del metabolismo energético. Su deficiencia (frecuente en vegetarianos o personas con malabsorción) se siente como fatiga sin causa aparente.</p>

      <h3>Cromo</h3>
      <p>Mejora la sensibilidad a la insulina, lo que estabiliza la glucosa y evita "subes y bajas" energéticos.</p>

      <h2 id="diferencia">Diferencias vs. Red Bull, Monster y similares</h2>
      <ul>
        <li><strong>Energía estable vs. pico y caída.</strong> Adaptógenos en lugar de azúcar.</li>
        <li><strong>Sin acidez destructiva.</strong> No daña esmalte ni mucosa gástrica.</li>
        <li><strong>Sin dependencia escalonada.</strong> Puedes tomarlo días salteados sin que el cuerpo "pida más".</li>
        <li><strong>Beneficio cognitivo real.</strong> No solo estimulación, sino foco sostenido.</li>
      </ul>

      <Callout>
        <strong>Comparativa rápida:</strong> Red Bull = estimulación bruta. XTRIVA = energía + foco + regulación de estrés. Distintas categorías de producto.
      </Callout>

      <h2 id="para-quien">Para quién está pensado</h2>
      <ul>
        <li><strong>Profesionales con jornadas largas</strong> (consultores, médicos de guardia, abogados, emprendedores).</li>
        <li><strong>Estudiantes</strong> en temporadas de exámenes o tesis.</li>
        <li><strong>Personas que quieren reducir su consumo de café</strong> sin perder rendimiento.</li>
        <li><strong>Deportistas que entrenan por la tarde/noche</strong> y necesitan empuje sin afectar el sueño (siempre que no se tome muy tarde).</li>
        <li><strong>Quienes manejan estrés crónico</strong> y necesitan adaptógenos.</li>
      </ul>

      <h2 id="como-tomarlo">Cómo y cuándo tomarlo</h2>
      <ul>
        <li><strong>1 sobre disuelto en 250-350 ml de agua fría.</strong></li>
        <li><strong>Antes</strong> de la actividad mental o física exigente (ideal 20-30 min antes).</li>
        <li><strong>Evitar después de las 4-5 p. m.</strong> para no afectar el sueño.</li>
        <li><strong>Máximo 1-2 sobres al día.</strong> No más.</li>
        <li>Si eres muy sensible a la cafeína, empieza con medio sobre.</li>
      </ul>

      <h2 id="transicion">Cómo hacer la transición desde el café/energéticos</h2>
      <p>Si vienes de tomar 4-6 cafés al día o 2-3 energéticos, no cortes de golpe (te dará dolor de cabeza). Haz una transición progresiva:</p>
      <ol>
        <li><strong>Semana 1:</strong> sustituye el primer energético/café por XTRIVA. Mantén los demás.</li>
        <li><strong>Semana 2:</strong> sustituye otro más. Aumenta agua.</li>
        <li><strong>Semana 3:</strong> el tercero. Suma hábitos: sol matutino, ejercicio breve.</li>
        <li><strong>Semana 4:</strong> consolida un máximo de 1-2 cafés naturales + 1 XTRIVA al día.</li>
      </ol>

      <Quote author="Sarai Núñez">
        La fatiga no se vence con más estimulantes. Se vence cuidando las baterías del cuerpo: sueño, mitocondrias y glándulas suprarrenales.
      </Quote>

      <p>Si quieres una estrategia completa para recuperar energía sostenida (no solo "parches"), <Link to="/#quiz">haz nuestro quiz wellness</Link>. Y para soporte mitocondrial profundo, lee también <Link to="/blog/omega-gen-v-5-en-1">la guía de Omega Gen V</Link>.</p>
    </>
  ),

  /* =================================================================
     10. Omega Gen V: el 5 en 1
     ================================================================= */
  "omega-gen-v-5-en-1": (
    <>
      <p>Si tuviera que elegir <strong>un solo suplemento</strong> para acompañar a Immunocal en cualquier protocolo de longevidad, sería el omega-3. La evidencia científica que respalda sus beneficios es <strong>abrumadora</strong>: miles de estudios, decenas de meta-análisis. <strong>Omega Gen V</strong> va un paso más allá: combina omega-3 marino premium con 4 nutrientes que potencian sus efectos. Un solo producto, cinco beneficios sinérgicos.</p>

      <TOC items={[
        { id: "por-que-omega3", label: "Por qué el omega-3 importa tanto" },
        { id: "ingredientes", label: "Los 5 ingredientes y por qué juntos" },
        { id: "beneficios", label: "Los 5 beneficios principales" },
        { id: "diferencia", label: "Qué hace diferente a Omega Gen V" },
        { id: "para-quien", label: "Para quién es ideal" },
        { id: "como-tomarlo", label: "Dosis y cómo tomarlo" },
        { id: "errores", label: "Errores frecuentes al elegir omega-3" },
      ]} />

      <h2 id="por-que-omega3">Por qué el omega-3 importa tanto</h2>
      <p>La dieta moderna está catastróficamente desbalanceada en su ratio omega-6/omega-3. Lo óptimo evolutivo sería <strong>2:1 o 4:1</strong>. La dieta promedio occidental llega a <strong>20:1 o incluso 30:1</strong> a favor del omega-6. ¿La consecuencia? Inflamación crónica de bajo grado en prácticamente todo el cuerpo, que es el motor de:</p>
      <ul>
        <li>Enfermedad cardiovascular.</li>
        <li>Diabetes tipo 2.</li>
        <li>Depresión y ansiedad.</li>
        <li>Artritis, dolor articular crónico.</li>
        <li>Deterioro cognitivo.</li>
        <li>Acné, dermatitis, psoriasis.</li>
      </ul>
      <p>Los omega-3 EPA y DHA <strong>restauran el balance</strong> y bajan la inflamación sistémica. Es uno de los suplementos con mayor "retorno de inversión biológica".</p>

      <h2 id="ingredientes">Los 5 ingredientes y por qué juntos</h2>

      <h3>1. Omega-3 EPA + DHA</h3>
      <p>Del aceite de pescado purificado (sin metales pesados, sin oxidación). EPA destaca para inflamación y estado de ánimo; DHA para cerebro y vista.</p>

      <h3>2. Vitamina D3</h3>
      <p>Hormona-vitamina maestra de la inmunidad. Se absorbe mejor con grasa, por lo que combinarla con omega-3 es ideal. Modula el sistema inmune y reduce inflamación.</p>

      <h3>3. Vitamina K2</h3>
      <p>Asegura que el calcio movilizado por la D3 vaya a los <strong>huesos</strong> y no a las <strong>arterias</strong>. Esta combinación D3 + K2 es uno de los <em>standards</em> modernos en suplementación de calidad.</p>

      <h3>4. Coenzima Q10</h3>
      <p>Antioxidante mitocondrial. Especialmente útil después de los 40 (su producción endógena cae) y para quienes toman estatinas (que la depleten). Apoya energía y salud cardiovascular.</p>

      <h3>5. Resveratrol</h3>
      <p>El polifenol del vino tinto, pero en dosis útil. Activa sirtuinas (genes asociados a longevidad), tiene efecto antioxidante y antinflamatorio.</p>

      <Callout>
        <strong>El argumento de combinar:</strong> tomar omega-3 sin K2 ni CoQ10 ni D3 es como tener un buen motor sin aceite ni gasolina premium. La sinergia es la que rinde.
      </Callout>

      <h2 id="beneficios">Los 5 beneficios principales</h2>

      <h3>1. Salud cerebral y memoria</h3>
      <p>El DHA es estructural en las membranas neuronales. Suplementación regular se asocia con menor declive cognitivo, mejor memoria de trabajo y reducción de riesgo de demencia.</p>

      <h3>2. Salud cardiovascular</h3>
      <p>Reduce triglicéridos, mejora HDL, baja presión arterial leve a moderada y reduce la oxidación del LDL. La combinación EPA + CoQ10 es especialmente útil.</p>

      <h3>3. Reducción de inflamación crónica</h3>
      <p>EPA es precursor de resolvinas y protectinas — mensajeros que <em>apagan</em> la inflamación. Útil en dolor articular, autoinmunidad, recuperación deportiva.</p>

      <h3>4. Mejor estado de ánimo</h3>
      <p>Múltiples meta-análisis muestran efecto antidepresivo modesto pero consistente del EPA, especialmente en dosis &gt;1 g/día.</p>

      <h3>5. Soporte óseo y articular</h3>
      <p>La combinación D3 + K2 + omega-3 protege la matriz ósea y la salud cartilaginosa. Particularmente útil &gt;40 años.</p>

      <h2 id="diferencia">Qué hace diferente a Omega Gen V</h2>
      <ul>
        <li><strong>Pureza farmacéutica:</strong> destilado molecular, sin mercurio ni dioxinas.</li>
        <li><strong>Concentración alta de EPA + DHA</strong> por cápsula.</li>
        <li><strong>Sinergia 5-en-1:</strong> no necesitas comprar 5 frascos separados.</li>
        <li><strong>Sin reflujo ni "aliento a pescado"</strong> (problema común de aceites baratos).</li>
        <li><strong>Respaldo Immunotec:</strong> calidad farmacéutica, no industria masiva.</li>
      </ul>

      <h2 id="para-quien">Para quién es ideal</h2>
      <ul>
        <li>Adultos &gt;30 que quieren prevención cardiovascular y cognitiva.</li>
        <li>Personas con dolor articular o inflamación crónica.</li>
        <li>Quienes pasan por temporadas de bajo estado de ánimo.</li>
        <li>Mujeres en perimenopausia/menopausia (apoyo cardiovascular y óseo).</li>
        <li>Atletas en busca de recuperación más rápida.</li>
        <li>Personas que consumen poco pescado azul (atún, salmón, sardina).</li>
        <li>Vegetarianos con cuidado (consultar fuente vegetal alternativa de DHA).</li>
      </ul>

      <h2 id="como-tomarlo">Dosis y cómo tomarlo</h2>
      <ul>
        <li><strong>1-2 cápsulas al día con la comida principal.</strong></li>
        <li><strong>Con grasa</strong> (mejora absorción de D3, K2 y CoQ10, todas liposolubles).</li>
        <li>Si tomas anticoagulantes, consulta con tu médico (omega-3 y vit. K interactúan).</li>
        <li>Sostener mínimo <strong>3 meses</strong> para ver efectos completos.</li>
      </ul>

      <h2 id="errores">Errores frecuentes al elegir omega-3</h2>
      <ol>
        <li><strong>Comprar el más barato.</strong> Suele estar oxidado y aporta más daño que beneficio.</li>
        <li><strong>Quedarse en aceite de hígado de bacalao genérico.</strong> Pobre en EPA/DHA real.</li>
        <li><strong>Mirar solo "1000 mg de aceite":</strong> lo que importa son los miligramos de EPA + DHA, no el total de aceite.</li>
        <li><strong>Olvidar que necesita vit. K2.</strong> D3 sola puede desviar calcio a arterias.</li>
        <li><strong>Esperar resultados en 1 semana.</strong> El cambio en membranas celulares toma 8-12 semanas.</li>
      </ol>

      <Quote author="Sarai Núñez">
        Si Immunocal es el <em>antioxidante</em> de tu protocolo, Omega Gen V es el <em>antiinflamatorio</em>. Juntos cubren el 80 % del trabajo metabólico.
      </Quote>

      <p>Para un protocolo completo de longevidad combinado con Immunocal, <Link to="/#quiz">haz nuestro quiz wellness gratis</Link>. Y si quieres entender por qué reducir inflamación es tan importante, lee <Link to="/blog/estres-oxidativo-explicado">Estrés oxidativo explicado</Link>.</p>
    </>
  ),
};

export default ARTICLE_CONTENT_3;
